"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isInterviewSessionPath } from "@/lib/paths";
import { useMemberNavVisibility } from "@/hooks/useMemberNavVisibility";
import {
  useCollapsibleSidebar,
  resetCollapsedCache,
} from "@/hooks/useCollapsibleSidebar";
import NavBadge from "./NavBadge";
import {
  getMemberNavBadge,
  getVisibleMemberNavItems,
  isMemberNavItemActive,
} from "./memberNavItems";

// 会員向け画面（(app) 配下）の PC でのグローバルナビ。
// モバイルでは描画せず、ヘッダー直下の横並びナビ（BottomNav）が同じ5項目を担う。
//
// (app)/layout.tsx の flex 行の中に置き、内容と横並びにする。fixed にすると
// 幅をコンテンツ側へ持ち回る必要が出るうえ、フッターの左端を覆う。
const COLLAPSED_STORAGE_KEY = "appSidebarCollapsed";

export default function AppSidebar() {
  const pathname = usePathname();
  const { user, loading } = useAuth();
  // 表示条件（求人区分・大学連携）は1つのフックでまとめて解決し、3つの配置面で同じ入力を渡す
  const navVisibility = useMemberNavVisibility();
  const { collapsed, restored, toggle } = useCollapsibleSidebar(
    COLLAPSED_STORAGE_KEY,
  );

  // 面接セッション中はナビを隠し、面接官 AI に集中できる没入表示にする
  if (isInterviewSessionPath(pathname)) return null;

  // (app) 配下でもミドルウェアの保護対象外のページ（/practice/select・/interview/setup・
  // /aptitude 等）は未ログインでも開ける。会員専用の行き先だけを並べたメニューを
  // 未ログインに見せても押せる先がないため、ここでは出さない。
  // 移行前のヘッダーもナビリンクをログイン済みに限っており、その挙動を引き継ぐ。
  if (loading || !user) return null;

  const items = getVisibleMemberNavItems("sidebar", navVisibility);

  return (
    <nav
      aria-label="メインメニュー"
      data-testid="app_sidebar"
      // 縮小 112px / 展開 256px。トグルが右境界線をまたいで外へはみ出すため overflow は切らない。
      className={`hidden shrink-0 self-start md:sticky md:top-0 md:flex md:h-screen md:flex-col border-r border-border bg-surface ${
        // 保存値を読み終えるまで遷移を止める。初回描画は展開状態から始まるため、
        // 遷移を許すと縮小状態のユーザーに幅が縮む様子が見えてしまう。
        restored ? "transition-[width]" : ""
      } ${collapsed ? "md:w-28" : "md:w-64"}`}
    >
      <div
        className={`flex shrink-0 items-center py-4 ${
          collapsed ? "justify-center px-4" : "px-5"
        }`}
      >
        <Link
          href="/dashboard"
          className="flex items-center"
          aria-label="ダッシュボードへ"
        >
          {/* 縦組みロゴは元から幅が狭いため、縮小時もマークへ差し替えず同じものを使う */}
          <Image
            src="/logo_menren_v.svg"
            alt="MENREN"
            width={240}
            height={180}
            className="h-12 w-auto"
            priority
          />
        </Link>
      </div>

      {/* トグルは右境界線の上に重ねる。ul を relative の基準にすると項目の高さに合わせやすい。 */}
      <div className="relative flex-1">
        <ul>
          {items.map((item) => {
            const active = isMemberNavItemActive(item, pathname);
            const badge = getMemberNavBadge(item, navVisibility);
            return (
              <li key={item.href}>
                <Link
                  href={item.href}
                  {...(active ? { "aria-current": "page" as const } : {})}
                  {...(collapsed
                    ? { title: item.label, "aria-label": item.label }
                    : {})}
                  // 現在地の背景は左右に余白を残さず行の全幅を塗る
                  className={`relative font-bold flex min-h-14 items-center gap-3 transition-colors ${
                    collapsed ? "justify-center px-4" : "px-5"
                  } ${
                    active
                      ? "bg-surface-container text-on-surface"
                      : "text-on-surface hover:bg-surface-container-low"
                  }`}
                >
                  {/* 縮小時はラベルが無いので、バッジはアイコンの右肩に付ける */}
                  <span className="relative flex shrink-0">
                    {item.renderIcon?.({
                      stroke: "currentColor",
                      className: "h-5 w-5",
                    })}
                    {collapsed && badge && (
                      <NavBadge
                        label={badge.label}
                        tone={badge.tone}
                        className="-top-1.5 -right-2.5 px-1 py-px"
                      />
                    )}
                  </span>
                  {!collapsed && (
                    // 展開時はバッジをラベルの右に並べる。右肩に重ねると「共有中」のような
                    // 幅のある文言がラベルの文字に被る
                    <span className="flex min-w-0 items-center">
                      <span className="block truncate">{item.label}</span>
                      {badge && (
                        <NavBadge
                          label={badge.label}
                          tone={badge.tone}
                          placement="inline"
                          className="px-1.5 py-px"
                        />
                      )}
                    </span>
                  )}
                  {active && (
                    <span
                      data-testid={`app_sidebar.active_bar.${item.href}`}
                      aria-hidden="true"
                      className="absolute inset-y-0 right-0 w-1.5 bg-gradient-to-b from-primary to-accent"
                    />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* 2番目の項目のあたりに置く。行の高さ 56px * 1.5 = 84px が中心。
            現在地の縦バーと同じ座標に来ることがあるため、不透明にして手前へ重ねる。 */}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? "メニューを広げる" : "メニューを折りたたむ"}
          data-testid="app_sidebar.toggle"
          className="absolute -right-3 top-28 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-surface text-on-surface-variant shadow-sm transition-colors hover:bg-surface-container-low"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>
    </nav>
  );
}

// テストが描画をまたいで折りたたみ状態を持ち越さないようにするための後始末。
export { resetCollapsedCache, COLLAPSED_STORAGE_KEY };
